import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiKey, FiPlus, FiTrash2, FiCopy, FiCheck, FiX, FiToggleLeft, FiToggleRight, FiEdit2 } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

const ABILITY_GROUPS: Record<string, { label: string; label_ar: string; abilities: string[] }> = {
  orders: { label: 'Orders', label_ar: 'الطلبات', abilities: ['orders:read', 'orders:write'] },
  products: { label: 'Products', label_ar: 'المنتجات', abilities: ['products:read', 'products:write'] },
  categories: { label: 'Categories', label_ar: 'الفئات', abilities: ['categories:read', 'categories:write'] },
  customers: { label: 'Customers', label_ar: 'العملاء', abilities: ['customers:read', 'customers:write'] },
  delivery_areas: { label: 'Delivery Areas', label_ar: 'مناطق التوصيل', abilities: ['delivery_areas:read'] },
  payment_gateways: { label: 'Payment Gateways', label_ar: 'بوابات الدفع', abilities: ['payment_gateways:read'] },
  promo_codes: { label: 'Promo Codes', label_ar: 'أكواد الخصم', abilities: ['promo_codes:read', 'promo_codes:write'] },
};

const ABILITY_LABELS: Record<string, { en: string; ar: string }> = {
  'orders:read': { en: 'Read', ar: 'قراءة' },
  'orders:write': { en: 'Write', ar: 'كتابة' },
  'products:read': { en: 'Read', ar: 'قراءة' },
  'products:write': { en: 'Write', ar: 'كتابة' },
  'categories:read': { en: 'Read', ar: 'قراءة' },
  'categories:write': { en: 'Write', ar: 'كتابة' },
  'customers:read': { en: 'Read', ar: 'قراءة' },
  'customers:write': { en: 'Write', ar: 'كتابة' },
  'delivery_areas:read': { en: 'Read', ar: 'قراءة' },
  'payment_gateways:read': { en: 'Read', ar: 'قراءة' },
  'promo_codes:read': { en: 'Read', ar: 'قراءة' },
  'promo_codes:write': { en: 'Write', ar: 'كتابة' },
};

export default function ApiAccess() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [tokens, setTokens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState('');
  const [form, setForm] = useState({ name: '', abilities: [] as string[], expires_at: '' });
  const [tab, setTab] = useState<'tokens' | 'docs'>('tokens');
  const [editingToken, setEditingToken] = useState<any>(null);
  const [editForm, setEditForm] = useState({ name: '', abilities: [] as string[] });
  const [savingEdit, setSavingEdit] = useState(false);

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;

  useEffect(() => {
    if (store?.id) loadTokens();
  }, [store?.id]);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/stores/${store.id}/api-tokens`);
      setTokens(res.data.tokens || []);
    } catch {} finally { setLoading(false); }
  };

  const toggleAbility = (ability: string) => {
    setForm(prev => ({
      ...prev,
      abilities: prev.abilities.includes(ability)
        ? prev.abilities.filter(a => a !== ability)
        : [...prev.abilities, ability],
    }));
  };

  const selectAllAbilities = () => {
    const all = Object.values(ABILITY_GROUPS).flatMap(g => g.abilities);
    setForm(prev => ({ ...prev, abilities: all }));
  };

  const clearAllAbilities = () => {
    setForm(prev => ({ ...prev, abilities: [] }));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error(t('Enter a token name', 'أدخل اسم للتوكن')); return; }
    if (form.abilities.length === 0) { toast.error(t('Select at least one permission', 'اختر صلاحية واحدة على الأقل')); return; }
    setCreating(true);
    try {
      const res = await api.post(`/stores/${store.id}/api-tokens`, {
        name: form.name,
        abilities: form.abilities,
        expires_at: form.expires_at || null,
      });
      setNewToken(res.data.plain_token);
      setForm({ name: '', abilities: [], expires_at: '' });
      setShowCreate(false);
      loadTokens();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setCreating(false); }
  };

  const handleToggleActive = async (token: any) => {
    try {
      await api.put(`/stores/${store.id}/api-tokens/${token.id}`, { is_active: !token.is_active });
      setTokens(prev => prev.map(t => t.id === token.id ? { ...t, is_active: !t.is_active } : t));
    } catch { toast.error('Error'); }
  };

  const handleDelete = async (token: any) => {
    if (!window.confirm(t(`Revoke token "${token.name}"?`, `حذف التوكن "${token.name}"؟`))) return;
    try {
      await api.delete(`/stores/${store.id}/api-tokens/${token.id}`);
      toast.success(t('Token revoked', 'تم حذف التوكن'));
      loadTokens();
    } catch { toast.error('Error'); }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(newToken);
    toast.success(t('Token copied', 'تم نسخ التوكن'));
  };

  const openEditToken = (token: any) => {
    setEditingToken(token);
    setEditForm({ name: token.name, abilities: token.abilities || [] });
  };

  const handleEditSave = async () => {
    if (!editingToken) return;
    setSavingEdit(true);
    try {
      await api.put(`/stores/${store.id}/api-tokens/${editingToken.id}`, editForm);
      toast.success(t('Token updated', 'تم تحديث التوكن'));
      setEditingToken(null);
      loadTokens();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSavingEdit(false); }
  };

  const toggleEditAbility = (ability: string) => {
    setEditForm(prev => ({
      ...prev,
      abilities: prev.abilities.includes(ability)
        ? prev.abilities.filter(a => a !== ability)
        : [...prev.abilities, ability],
    }));
  };

  if (!store) return null;

  return (
    <div>
      <div className="page-header">
        <h1><FiKey style={{ marginInlineEnd: 8 }} /> {t('API Access', 'الوصول للـ API')}</h1>
        {tab === 'tokens' && (
          <button className="btn btn-primary" onClick={() => { setShowCreate(true); setNewToken(''); }}>
            <FiPlus /> {t('Create Token', 'إنشاء توكن')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--gray-100)' }}>
        <button onClick={() => setTab('tokens')} style={{
          padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 14, fontWeight: 600, color: tab === 'tokens' ? 'var(--primary)' : 'var(--gray-400)',
          borderBottom: tab === 'tokens' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: -2,
        }}>
          <FiKey style={{ marginBottom: -2, marginInlineEnd: 6 }} /> {t('Tokens', 'التوكنات')}
        </button>
        <button onClick={() => setTab('docs')} style={{
          padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 14, fontWeight: 600, color: tab === 'docs' ? 'var(--primary)' : 'var(--gray-400)',
          borderBottom: tab === 'docs' ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: -2,
        }}>
          {t('Documentation', 'التوثيق')}
        </button>
      </div>

      {tab === 'docs' && <ApiDocs store={store} lang={lang} t={t} />}

      {tab === 'tokens' && <>
      {/* New token display */}
      {newToken && (
        <div className="card" style={{ padding: 20, marginBottom: 20, background: '#f0fdf4', border: '2px solid #16a34a' }}>
          <h3 style={{ margin: '0 0 8px', color: '#16a34a', fontSize: 15 }}>
            <FiCheck style={{ marginInlineEnd: 6 }} /> {t('Token Created!', 'تم إنشاء التوكن!')}
          </h3>
          <p style={{ fontSize: 13, color: '#15803d', margin: '0 0 12px' }}>
            {t('Copy this token now. It won\'t be shown again.', 'انسخ هذا التوكن الآن. لن يظهر مرة أخرى.')}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{
              flex: 1, padding: '10px 14px', background: 'white', borderRadius: 8,
              border: '1px solid #bbf7d0', fontSize: 13, fontFamily: 'monospace',
              wordBreak: 'break-all', direction: 'ltr',
            }}>
              {newToken}
            </code>
            <button className="btn btn-primary btn-sm" onClick={copyToken}>
              <FiCopy size={14} /> {t('Copy', 'نسخ')}
            </button>
          </div>
        </div>
      )}

      {/* Token list */}
      {loading ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : tokens.length === 0 && !showCreate ? (
        <div className="empty-state">
          <FiKey size={40} />
          <h3>{t('No API Tokens', 'لا يوجد توكنات')}</h3>
          <p>{t('Create a token to access your store data via API', 'أنشئ توكن للوصول لبيانات متجرك عبر الـ API')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tokens.map(token => (
            <div key={token.id} className="card" style={{ padding: 16, opacity: token.is_active ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <strong style={{ fontSize: 15 }}>{token.name}</strong>
                    <code style={{ fontSize: 11, color: 'var(--gray-400)', background: 'var(--gray-50)', padding: '2px 8px', borderRadius: 4 }}>
                      {token.token_preview}
                    </code>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 50,
                      background: token.is_active ? '#dcfce7' : '#fee2e2',
                      color: token.is_active ? '#16a34a' : '#dc2626',
                      fontWeight: 600,
                    }}>
                      {token.is_active ? t('Active', 'نشط') : t('Disabled', 'معطل')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                    {(token.abilities || []).map((a: string) => (
                      <span key={a} style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 4,
                        background: 'var(--gray-50)', color: 'var(--gray-500)',
                      }}>
                        {a}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                    {t('Created', 'أنشئ')} {new Date(token.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}
                    {token.last_used_at && <> · {t('Last used', 'آخر استخدام')} {new Date(token.last_used_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}</>}
                    {token.expires_at && <> · {t('Expires', 'ينتهي')} {new Date(token.expires_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-white" onClick={() => openEditToken(token)} title={t('Edit', 'تعديل')}>
                    <FiEdit2 size={14} />
                  </button>
                  <button className="btn btn-sm btn-white" onClick={() => handleToggleActive(token)}
                    title={token.is_active ? t('Disable', 'تعطيل') : t('Enable', 'تفعيل')}
                    style={{ color: token.is_active ? '#f59e0b' : '#16a34a' }}>
                    {token.is_active ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                  </button>
                  <button className="btn btn-sm btn-white" onClick={() => handleDelete(token)} style={{ color: 'var(--danger)' }}>
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      </>}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => !creating && setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
            <h2><FiKey style={{ marginInlineEnd: 8 }} /> {t('Create API Token', 'إنشاء توكن API')}</h2>

            <div className="form-group">
              <label className="form-label">{t('Token Name', 'اسم التوكن')} *</label>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder={t('e.g. Mobile App, Integration', 'مثال: تطبيق موبايل، تكامل')} />
            </div>

            <div className="form-group">
              <label className="form-label">{t('Expiry Date', 'تاريخ الانتهاء')} ({t('optional', 'اختياري')})</label>
              <input type="date" className="form-input" value={form.expires_at}
                onChange={e => setForm({ ...form, expires_at: e.target.value })}
                min={new Date().toISOString().split('T')[0]} />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>{t('Permissions', 'الصلاحيات')} *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={selectAllAbilities}
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', background: 'white', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {t('Select All', 'تحديد الكل')}
                  </button>
                  <button type="button" onClick={clearAllAbilities}
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', background: 'white', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {t('Clear', 'مسح')}
                  </button>
                </div>
              </div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
                {Object.entries(ABILITY_GROUPS).map(([key, group]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--gray-100)', gap: 12 }}>
                    <strong style={{ fontSize: 13, width: 140, flexShrink: 0 }}>
                      {lang === 'ar' ? group.label_ar : group.label}
                    </strong>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {group.abilities.map(ability => {
                        const isSelected = form.abilities.includes(ability);
                        const label = ABILITY_LABELS[ability];
                        return (
                          <label key={ability} style={{
                            display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                            borderRadius: 6, border: '1px solid', fontSize: 12, cursor: 'pointer',
                            borderColor: isSelected ? 'var(--primary)' : 'var(--gray-200)',
                            background: isSelected ? 'var(--primary)' : 'white',
                            color: isSelected ? 'white' : 'var(--gray-600)',
                          }}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggleAbility(ability)} style={{ display: 'none' }} />
                            {lang === 'ar' ? label.ar : label.en}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button className="btn btn-white" onClick={() => setShowCreate(false)}>{dt('cancel', lang)}</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? '...' : t('Create Token', 'إنشاء توكن')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit modal */}
      {editingToken && (
        <div className="modal-overlay" onClick={() => setEditingToken(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
            <h2>{t('Edit Token', 'تعديل التوكن')}</h2>
            <div className="form-group">
              <label className="form-label">{t('Token Name', 'اسم التوكن')}</label>
              <input className="form-input" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('Permissions', 'الصلاحيات')}</label>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
                {Object.entries(ABILITY_GROUPS).map(([key, group]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--gray-100)', gap: 12 }}>
                    <strong style={{ fontSize: 13, width: 140, flexShrink: 0 }}>{lang === 'ar' ? group.label_ar : group.label}</strong>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {group.abilities.map(ability => {
                        const isSelected = editForm.abilities.includes(ability);
                        return (
                          <label key={ability} style={{
                            display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6,
                            border: '1px solid', fontSize: 12, cursor: 'pointer',
                            borderColor: isSelected ? 'var(--primary)' : 'var(--gray-200)',
                            background: isSelected ? 'var(--primary)' : 'white',
                            color: isSelected ? 'white' : 'var(--gray-600)',
                          }}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggleEditAbility(ability)} style={{ display: 'none' }} />
                            {lang === 'ar' ? ABILITY_LABELS[ability].ar : ABILITY_LABELS[ability].en}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button className="btn btn-white" onClick={() => setEditingToken(null)}>{dt('cancel', lang)}</button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={savingEdit}>
                {savingEdit ? '...' : dt('save_changes', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApiDocs({ store, lang, t }: { store: any; lang: string; t: (en: string, ar: string) => string }) {
  const baseUrl = 'https://cartz.cc/api';
  const slug = store?.slug || 'your-store';
  const storeId = store?.id || '{store_id}';
  const [expandedEndpoint, setExpandedEndpoint] = useState<number | null>(null);

  const codeBlock = (code: string) => (
    <pre style={{ padding: 14, background: '#1e293b', color: '#e2e8f0', borderRadius: 8, fontSize: 12, direction: 'ltr', fontFamily: 'monospace', overflowX: 'auto', margin: '8px 0', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
      {code}
    </pre>
  );

  const badge = (text: string, color: string) => (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: color + '18', color, fontFamily: 'monospace', marginInlineStart: 4 }}>{text}</span>
  );

  const fieldRow = (name: string, type: string, required: boolean, desc: string) => (
    <tr key={name} style={{ borderBottom: '1px solid var(--gray-50)' }}>
      <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{name}</td>
      <td style={{ padding: '6px 10px', fontSize: 11, color: 'var(--gray-500)' }}>{type}</td>
      <td style={{ padding: '6px 10px', textAlign: 'center' }}>{required ? badge('required', '#dc2626') : badge('optional', '#6b7280')}</td>
      <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--gray-500)' }}>{desc}</td>
    </tr>
  );

  const endpoints = [
    {
      method: 'GET', path: `/storefront/${slug}`, desc: t('Get store info', 'معلومات المتجر'), ability: null, auth: false,
      response: `{
  "name": "Store Name",
  "slug": "${slug}",
  "description": "...",
  "logo": "stores/3/logo/logo.png",
  "primary_color": "#3b82f6",
  "phone": "+965...",
  "currency_symbol": "KD",
  "delivery_enabled": true,
  "pickup_enabled": true
}`,
    },
    {
      method: 'GET', path: `/storefront/${slug}/categories`, desc: t('List active categories', 'قائمة الفئات النشطة'), ability: 'categories:read', auth: false,
      response: `[
  {
    "id": 1,
    "name": "Electronics",
    "name_ar": "إلكترونيات",
    "slug": "electronics",
    "image": "categories/img.jpg",
    "products_count": 15,
    "children": [{ "id": 2, "name": "Phones", ... }]
  }
]`,
    },
    {
      method: 'GET', path: `/storefront/${slug}/products`, desc: t('List products (paginated)', 'قائمة المنتجات (مقسمة)'), ability: 'products:read', auth: false,
      queryParams: [
        { name: 'category', type: 'string', desc: t('Filter by category slug', 'تصفية حسب slug الفئة') },
        { name: 'search', type: 'string', desc: t('Search by name, SKU', 'بحث بالاسم أو SKU') },
        { name: 'tag', type: 'string', desc: t('Filter by tag', 'تصفية حسب الوسم') },
        { name: 'min_price', type: 'number', desc: t('Minimum price', 'الحد الأدنى للسعر') },
        { name: 'max_price', type: 'number', desc: t('Maximum price', 'الحد الأقصى للسعر') },
        { name: 'per_page', type: 'integer', desc: t('Items per page (default 30, max 100)', 'عدد العناصر (افتراضي 30)') },
        { name: 'page', type: 'integer', desc: t('Page number', 'رقم الصفحة') },
      ],
      response: `{
  "data": [
    {
      "id": 1,
      "name": "Product Name",
      "name_ar": "اسم المنتج",
      "slug": "product-name",
      "price": "5.000",
      "compare_price": "7.000",
      "effective_price": 5.0,
      "image": "products/img.jpg",
      "is_active": true,
      "is_featured": false,
      "category": { "id": 1, "name": "..." }
    }
  ],
  "total": 150,
  "has_more": true,
  "max_price": 25.0
}`,
    },
    {
      method: 'GET', path: `/storefront/${slug}/products/{id}`, desc: t('Get single product', 'تفاصيل منتج'), ability: 'products:read', auth: false,
      response: `{
  "id": 1,
  "name": "Product Name",
  "price": "5.000",
  "description": "...",
  "image": "products/img.jpg",
  "additional_images": ["img2.jpg"],
  "option_groups": [
    {
      "name": "Size",
      "type": "single",
      "required": true,
      "choices": [{ "name": "Small", "price": "0.000" }, { "name": "Large", "price": "1.000" }]
    }
  ]
}`,
    },
    {
      method: 'GET', path: `/storefront/${slug}/delivery-areas`, desc: t('List active delivery areas', 'مناطق التوصيل النشطة'), ability: 'delivery_areas:read', auth: false,
      response: `[
  {
    "id": 253,
    "name": "السالمية",
    "name_en": "Salmiya",
    "delivery_fee": "1.000",
    "minimum_order": "5.000",
    "free_delivery_threshold": "15.000",
    "estimated_time": 1,
    "estimated_time_unit": "days",
    "business_days": true
  }
]`,
    },
    {
      method: 'GET', path: `/storefront/${slug}/payment-gateways`, desc: t('Enabled payment gateways', 'بوابات الدفع المفعلة'), ability: 'payment_gateways:read', auth: false,
      response: `[
  { "gateway": "cbk_knet", "name": "CBK KNET", "name_ar": "بنك التجاري كي نت" },
  { "gateway": "paypal", "name": "PayPal", "name_ar": "باي بال" }
]`,
    },
    {
      method: 'POST', path: `/storefront/${slug}/orders`, desc: t('Place a new order', 'تقديم طلب جديد'), ability: 'orders:write', auth: false,
      bodyFields: [
        { name: 'customer_name', type: 'string', required: true, desc: t('Customer full name', 'اسم العميل') },
        { name: 'customer_phone', type: 'string', required: true, desc: t('Customer phone number', 'هاتف العميل') },
        { name: 'customer_email', type: 'string', required: false, desc: t('Customer email', 'بريد العميل') },
        { name: 'type', type: '"delivery" | "pickup"', required: true, desc: t('Order type', 'نوع الطلب') },
        { name: 'payment_method', type: '"cash" | "online"', required: true, desc: t('Payment method', 'طريقة الدفع') },
        { name: 'delivery_address', type: 'string', required: false, desc: t('Delivery address (if delivery)', 'عنوان التوصيل') },
        { name: 'delivery_area', type: 'string', required: false, desc: t('Area name for delivery fee calc', 'اسم المنطقة لحساب التوصيل') },
        { name: 'notes', type: 'string', required: false, desc: t('Order notes', 'ملاحظات الطلب') },
        { name: 'promo_code', type: 'string', required: false, desc: t('Promo code to apply', 'كود الخصم') },
        { name: 'items', type: 'array', required: true, desc: t('Array of order items', 'مصفوفة عناصر الطلب') },
        { name: 'items[].product_id', type: 'integer', required: true, desc: t('Product ID', 'معرف المنتج') },
        { name: 'items[].quantity', type: 'integer', required: true, desc: t('Quantity', 'الكمية') },
        { name: 'items[].options', type: 'array', required: false, desc: t('Selected product options', 'خيارات المنتج المختارة') },
      ],
      requestExample: `{
  "customer_name": "Ahmed Ali",
  "customer_phone": "+96599999999",
  "customer_email": "ahmed@example.com",
  "type": "delivery",
  "payment_method": "cash",
  "delivery_address": "Block 5, St 10, House 3",
  "delivery_area": "السالمية",
  "promo_code": "SAVE10",
  "items": [
    { "product_id": 1, "quantity": 2 },
    { "product_id": 5, "quantity": 1, "options": [{ "name": "Size", "choice": "Large", "price": 1.0 }] }
  ]
}`,
      response: `{
  "id": 44,
  "order_number": "#1038",
  "customer_name": "Ahmed Ali",
  "subtotal": "15.000",
  "delivery_fee": "1.000",
  "discount": "1.500",
  "total": "14.500",
  "status": "pending",
  "payment_status": "pending",
  "items": [...]
}`,
    },
    {
      method: 'GET', path: `/stores/${storeId}/orders`, desc: t('List store orders (dashboard)', 'قائمة طلبات المتجر'), ability: 'orders:read', auth: true,
      queryParams: [
        { name: 'status', type: 'string', desc: t('pending, confirmed, preparing, ready, out_for_delivery, delivered, cancelled', 'الحالة') },
        { name: 'type', type: 'string', desc: t('"delivery" or "pickup"', 'delivery أو pickup') },
        { name: 'payment_status', type: 'string', desc: t('"pending", "paid", "failed"', 'حالة الدفع') },
        { name: 'payment_method', type: 'string', desc: t('"cash" or "online"', 'طريقة الدفع') },
        { name: 'date_from', type: 'date', desc: t('Start date (YYYY-MM-DD)', 'من تاريخ') },
        { name: 'date_to', type: 'date', desc: t('End date (YYYY-MM-DD)', 'إلى تاريخ') },
        { name: 'delivery_area', type: 'string', desc: t('Filter by area name', 'تصفية حسب المنطقة') },
        { name: 'customer_id', type: 'integer', desc: t('Filter by customer ID', 'تصفية حسب العميل') },
        { name: 'search', type: 'string', desc: t('Search by name, phone, order number', 'بحث بالاسم أو الهاتف أو رقم الطلب') },
        { name: 'per_page', type: 'integer', desc: t('Items per page (default 20, max 100)', 'عدد لكل صفحة') },
        { name: 'page', type: 'integer', desc: t('Page number', 'رقم الصفحة') },
      ],
      response: `{
  "data": [
    {
      "id": 44,
      "order_number": "#1038",
      "customer_name": "Ahmed",
      "customer_phone": "+965...",
      "customer_id": 12,
      "total": "14.500",
      "status": "pending",
      "payment_status": "paid",
      "payment_method": "online",
      "type": "delivery",
      "created_at": "2026-04-01T10:00:00Z",
      "items": [...]
    }
  ],
  "last_page": 5
}`,
    },
    {
      method: 'PUT', path: `/stores/${storeId}/orders/{order_id}/status`, desc: t('Update order status', 'تحديث حالة الطلب'), ability: 'orders:write', auth: true,
      bodyFields: [
        { name: 'status', type: 'string', required: true, desc: t('One of: pending, confirmed, preparing, ready, out_for_delivery, delivered, cancelled', 'الحالة الجديدة') },
      ],
      requestExample: `{ "status": "confirmed" }`,
      response: `{ "id": 44, "status": "confirmed", ... }`,
    },
    {
      method: 'GET', path: `/stores/${storeId}/customers`, desc: t('List store customers', 'قائمة العملاء'), ability: 'customers:read', auth: true,
      queryParams: [
        { name: 'search', type: 'string', desc: t('Search by name, email, phone', 'بحث بالاسم أو البريد أو الهاتف') },
        { name: 'page', type: 'integer', desc: t('Page number', 'رقم الصفحة') },
      ],
      response: `{
  "data": [
    {
      "id": 12,
      "name": "Ahmed Ali",
      "email": "ahmed@example.com",
      "phone": "+965...",
      "orders_count": 5,
      "created_at": "2026-03-15T..."
    }
  ]
}`,
    },
    {
      method: 'GET', path: `/stores/${storeId}/customers/{id}`, desc: t('Get customer with orders', 'تفاصيل العميل مع الطلبات'), ability: 'customers:read', auth: true,
      response: `{
  "customer": { "id": 12, "name": "Ahmed", "email": "...", "phone": "..." },
  "orders": [{ "id": 44, "order_number": "#1038", "total": "14.500", ... }]
}`,
    },
    {
      method: 'GET', path: `/stores/${storeId}/products`, desc: t('List products (dashboard)', 'قائمة المنتجات (لوحة التحكم)'), ability: 'products:read', auth: true,
      queryParams: [
        { name: 'search', type: 'string', desc: t('Search by name, SKU', 'بحث بالاسم أو SKU') },
        { name: 'category_id', type: 'integer', desc: t('Filter by category ID', 'تصفية حسب الفئة') },
        { name: 'status', type: '"active" | "inactive"', desc: t('Filter by status', 'تصفية حسب الحالة') },
        { name: 'per_page', type: 'integer', desc: t('Items per page', 'عدد لكل صفحة') },
      ],
      response: `{ "data": [...], "last_page": 3 }`,
    },
    {
      method: 'POST', path: `/stores/${storeId}/products`, desc: t('Create product', 'إنشاء منتج'), ability: 'products:write', auth: true,
      bodyFields: [
        { name: 'name', type: 'string', required: true, desc: t('Product name (English)', 'اسم المنتج (إنجليزي)') },
        { name: 'name_ar', type: 'string', required: false, desc: t('Product name (Arabic)', 'اسم المنتج (عربي)') },
        { name: 'category_id', type: 'integer', required: true, desc: t('Category ID', 'معرف الفئة') },
        { name: 'price', type: 'number', required: true, desc: t('Price (e.g. 5.000)', 'السعر') },
        { name: 'compare_price', type: 'number', required: false, desc: t('Compare/was price', 'السعر قبل الخصم') },
        { name: 'description', type: 'string', required: false, desc: t('Description', 'الوصف') },
        { name: 'sku', type: 'string', required: false, desc: t('SKU code', 'رمز SKU') },
        { name: 'is_active', type: 'boolean', required: false, desc: t('Active status (default true)', 'الحالة') },
        { name: 'image', type: 'file', required: false, desc: t('Product image (multipart)', 'صورة المنتج') },
      ],
      requestExample: `// Content-Type: multipart/form-data
{
  "name": "New Product",
  "name_ar": "منتج جديد",
  "category_id": 1,
  "price": 5.000,
  "sku": "PROD-001",
  "is_active": true
}`,
      response: `{ "id": 100, "name": "New Product", "slug": "new-product", ... }`,
    },
    {
      method: 'GET', path: `/stores/${storeId}/promo-codes`, desc: t('List promo codes', 'قائمة أكواد الخصم'), ability: 'promo_codes:read', auth: true,
      response: `[
  {
    "id": 1,
    "code": "SAVE10",
    "discount_type": "percentage",
    "discount_value": "10.000",
    "min_order_amount": "10.000",
    "usage_limit": 100,
    "usage_count": 23,
    "is_active": true,
    "start_date": "2026-03-01",
    "end_date": "2026-12-31"
  }
]`,
    },
  ];

  const methodColor = (m: string) => m === 'GET' ? '#16a34a' : m === 'POST' ? '#2563eb' : m === 'PUT' ? '#f59e0b' : '#ef4444';

  return (
    <div>
      {/* Auth section */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{t('Authentication', 'المصادقة')}</h3>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: '0 0 12px' }}>
          {t('Include your API token in the request headers:', 'أضف التوكن في هيدرات الطلب:')}
        </p>
        {codeBlock(`curl -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Accept: application/json" \\
     -H "Content-Type: application/json" \\
     ${baseUrl}/storefront/${slug}`)}
        <p style={{ fontSize: 12, color: 'var(--gray-400)', margin: '8px 0 0' }}>
          {t('Public endpoints (storefront/*) work without auth. Dashboard endpoints (stores/*) require authentication.', 'نقاط storefront/* عامة. نقاط stores/* تتطلب مصادقة.')}
        </p>
      </div>

      {/* Base URL */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>{t('Base URL', 'الرابط الأساسي')}</h3>
        {codeBlock(baseUrl)}
        <p style={{ fontSize: 12, color: 'var(--gray-400)', margin: '4px 0 0' }}>
          {t('Your store slug', 'slug متجرك')}: <code style={{ background: '#1e293b', color: '#e2e8f0', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{slug}</code>
          {' · '}Store ID: <code style={{ background: '#1e293b', color: '#e2e8f0', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{storeId}</code>
        </p>
      </div>

      {/* Endpoints */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {endpoints.map((ep: any, i) => {
          const isExpanded = expandedEndpoint === i;
          return (
            <div key={i} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                onClick={() => setExpandedEndpoint(isExpanded ? null : i)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', background: isExpanded ? 'var(--gray-50)' : 'white' }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 4, background: methodColor(ep.method) + '18', color: methodColor(ep.method), fontFamily: 'monospace', flexShrink: 0 }}>
                  {ep.method}
                </span>
                <code style={{ fontSize: 13, fontFamily: 'monospace', direction: 'ltr', flex: 1, color: 'var(--gray-700)' }}>{ep.path}</code>
                {ep.auth && badge('Auth', '#7c3aed')}
                {ep.ability && <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>{ep.ability}</span>}
                <span style={{ color: 'var(--gray-400)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>▾</span>
              </div>
              {isExpanded && (
                <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--gray-100)' }}>
                  <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: '12px 0' }}>{ep.desc}</p>

                  {/* Query params */}
                  {ep.queryParams && (
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Query Parameters', 'معاملات الاستعلام')}</strong>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginTop: 6 }}>
                        <tbody>
                          {ep.queryParams.map((p: any) => (
                            <tr key={p.name} style={{ borderBottom: '1px solid var(--gray-50)' }}>
                              <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{p.name}</td>
                              <td style={{ padding: '5px 10px', color: 'var(--gray-400)' }}>{p.type}</td>
                              <td style={{ padding: '5px 10px', color: 'var(--gray-500)' }}>{p.desc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Body fields */}
                  {ep.bodyFields && (
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Request Body (JSON)', 'جسم الطلب (JSON)')}</strong>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginTop: 6 }}>
                        <thead>
                          <tr style={{ background: 'var(--gray-50)' }}>
                            <th style={{ padding: '6px 10px', textAlign: lang === 'ar' ? 'right' : 'left', fontWeight: 600 }}>{t('Field', 'الحقل')}</th>
                            <th style={{ padding: '6px 10px', textAlign: lang === 'ar' ? 'right' : 'left', fontWeight: 600 }}>{t('Type', 'النوع')}</th>
                            <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600 }}></th>
                            <th style={{ padding: '6px 10px', textAlign: lang === 'ar' ? 'right' : 'left', fontWeight: 600 }}>{t('Description', 'الوصف')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ep.bodyFields.map((f: any) => fieldRow(f.name, f.type, f.required, f.desc))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Request example */}
                  {ep.requestExample && (
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Request Example', 'مثال الطلب')}</strong>
                      {codeBlock(ep.requestExample)}
                    </div>
                  )}

                  {/* Response */}
                  <div>
                    <strong style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t('Response', 'الاستجابة')}</strong>
                    {codeBlock(ep.response)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
